import * as THREE from 'three';
import { GameObject, ObjectType } from './GameObject';
import { SpatialManager } from '../optimization/SpatialManager';
import { CollisionSystem } from '../physics/CollisionSystem';

export class ObjectManager {
  private scene: THREE.Scene;
  private objects: Map<string, GameObject> = new Map();
  private staticObjects: Set<GameObject> = new Set();
  private dynamicObjects: Set<GameObject> = new Set();
  private spatialManager: SpatialManager;
  private collisionSystem: CollisionSystem;
  
  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.spatialManager = new SpatialManager(scene);
    this.collisionSystem = CollisionSystem.getInstance();
    
    // Connect the collision system to the spatial manager for efficient spatial queries
    this.collisionSystem.setSpatialManager(this.spatialManager);
    
    console.log('ObjectManager initialized');
  }
  
  public add(object: GameObject): void {
    // Add to objects map
    this.objects.set(object.getId(), object);
    
    // Add to specific type set
    if (object.getType() === ObjectType.STATIC) {
      this.staticObjects.add(object);
    } else {
      this.dynamicObjects.add(object);
    }
    
    // Add mesh to scene if it exists
    const mesh = object.getMesh();
    if (mesh) {
      this.scene.add(mesh);
    }
    
    // Add to spatial manager
    this.spatialManager.addObject(object);
    
    // Register with collision system if collidable
    if (object.isCollidableObject()) {
      this.collisionSystem.registerObject(object);
    }
  }
  
  /**
   * Check if an object with the given ID exists in the manager
   * @param objectId The ID of the object to check
   * @returns True if the object exists, false otherwise
   */
  public has(objectId: string): boolean {
    return this.objects.has(objectId);
  }
  
  public remove(objectId: string): void {
    const object = this.objects.get(objectId);
    
    if (!object) {
      console.warn(`Attempted to remove non-existent object with ID: ${objectId}`);
      return;
    }
    
    // Remove from objects map
    this.objects.delete(objectId);
    
    // Remove from type-specific set
    if (object.getType() === ObjectType.STATIC) {
      this.staticObjects.delete(object);
    } else {
      this.dynamicObjects.delete(object);
    }
    
    // Remove mesh from scene if it exists
    const mesh = object.getMesh();
    if (mesh) {
      this.scene.remove(mesh);
    }
    
    // Remove from spatial manager
    this.spatialManager.removeObject(objectId);
    
    // Unregister from collision system if collidable
    if (object.isCollidableObject()) {
      this.collisionSystem.unregisterObject(object);
    }
  }
  
  public update(deltaTime: number): void {
    // Update dynamic objects
    this.dynamicObjects.forEach(object => {
      if (object.isActivated()) {
        const oldPosition = object.getPosition().clone();
        
        // Update the object
        object.update(deltaTime);
        
        // Check if position changed
        const newPosition = object.getPosition();
        if (!oldPosition.equals(newPosition)) {
          // Update spatial partitioning if position changed
          this.spatialManager.updateObject(object);
        }
      }
    });
    
    // Update collision system
    this.collisionSystem.update(deltaTime);
  }
  
  public getObjectsInRadius(center: THREE.Vector3, radius: number): GameObject[] {
    return this.spatialManager.getObjectsInRadius(center, radius);
  }
  
  public getObjectsInArea(x: number, z: number, width: number, height: number): GameObject[] {
    return this.spatialManager.getObjectsInArea(x, z, width, height);
  }
  
  public toggleSpatialDebug(): void {
    this.spatialManager.toggleDebugVisualization();
  }
  
  public getSpatialStats(): { nodes: number, objects: number, maxDepth: number } {
    return this.spatialManager.getStats();
  }
  
  public getObject(objectId: string): GameObject | undefined {
    return this.objects.get(objectId);
  }
  
  public getAllObjects(): GameObject[] {
    return Array.from(this.objects.values());
  }
  
  public getStaticObjects(): GameObject[] {
    return Array.from(this.staticObjects);
  }
  
  public getDynamicObjects(): GameObject[] {
    return Array.from(this.dynamicObjects);
  }
  
  public getObjectCount(): number {
    return this.objects.size;
  }
  
  public getStaticObjectCount(): number {
    return this.staticObjects.size;
  }
  
  public getDynamicObjectCount(): number {
    return this.dynamicObjects.size;
  }
  
  /**
   * Get the spatial manager instance
   * @returns The spatial manager
   */
  public getSpatialManager(): SpatialManager {
    return this.spatialManager;
  }
}
